import {
  PTAC_PREFERENCE_AMPERAGES,
  PTAC_PREFERENCE_VOLTAGES,
  PTAC_UNKNOWN_ANSWER,
  PtacAmperageAnswer,
  PtacControlType,
  PtacExistingUnitsRecord,
  PtacGrilleCondition,
  PtacPreferencesRecord,
  PtacSleeveCondition,
  PtacVoltageAnswer
} from '@alpinehomeair/common';
import {
  PtacPreferencesAnswerOption,
  PtacSleeveSizeAnswer
} from 'utils/ptac-preferences-types';

export const PTAC_NOT_SURE_LABEL = 'Not sure';

export const PTAC_VOLTAGE_ANSWER_OPTIONS: PtacPreferencesAnswerOption<PtacVoltageAnswer>[] =
  [
    ...PTAC_PREFERENCE_VOLTAGES.map((value) => ({ value, label: value })),
    { value: PTAC_UNKNOWN_ANSWER, label: PTAC_NOT_SURE_LABEL }
  ];

export const PTAC_AMPERAGE_ANSWER_OPTIONS: PtacPreferencesAnswerOption<PtacAmperageAnswer>[] =
  [
    ...PTAC_PREFERENCE_AMPERAGES.map((value) => ({ value, label: value })),
    { value: PTAC_UNKNOWN_ANSWER, label: PTAC_NOT_SURE_LABEL }
  ];

export const PTAC_SLEEVE_SIZE_OPTIONS: PtacPreferencesAnswerOption<PtacSleeveSizeAnswer>[] =
  [
    { value: 'standard', label: '42 x 16 in. (standard)' },
    { value: 'other', label: 'Other or unknown' }
  ];

export const PTAC_SLEEVE_CONDITION_OPTIONS: PtacPreferencesAnswerOption<PtacSleeveCondition>[] =
  [
    { value: 'good', label: 'Good condition' },
    { value: 'replace', label: 'Corroded or 10+ years old' }
  ];

export const PTAC_GRILLE_OPTIONS: PtacPreferencesAnswerOption<PtacGrilleCondition>[] =
  [
    { value: 'keep', label: 'Keeping the current grille' },
    { value: 'replace', label: 'Missing or damaged' }
  ];

export const PTAC_CONTROL_OPTIONS: PtacPreferencesAnswerOption<PtacControlType>[] =
  [
    { value: 'unit', label: 'Controls on the unit' },
    { value: 'wall', label: 'Wall thermostat' }
  ];

export const EMPTY_PTAC_EXISTING_UNITS: PtacExistingUnitsRecord = {
  sleeveStandard: null,
  sleeveCondition: null,
  grille: null,
  control: null
};

export function isPtacExistingUnitsComplete(
  existing: PtacExistingUnitsRecord | undefined
): boolean {
  return (
    !!existing &&
    existing.sleeveStandard !== null &&
    existing.sleeveCondition !== null &&
    existing.grille !== null &&
    existing.control !== null
  );
}

export function isPtacPreferencesComplete(
  record: PtacPreferencesRecord | null | undefined
): boolean {
  return !!record?.voltage && !!record.amperage;
}

export function sleeveSizeAnswerOf(
  existing: PtacExistingUnitsRecord | undefined
): PtacSleeveSizeAnswer | null {
  if (!existing || existing.sleeveStandard === null) return null;
  return existing.sleeveStandard ? 'standard' : 'other';
}

export function withPtacReplacingAnswer(
  record: PtacPreferencesRecord,
  replacing: boolean
): PtacPreferencesRecord {
  if (!replacing) {
    const { existing, ...rest } = record;
    return { ...rest, replacing };
  }
  return {
    ...record,
    replacing,
    existing: record.existing ?? EMPTY_PTAC_EXISTING_UNITS
  };
}

export function withPtacExistingUnitsAnswer(
  record: PtacPreferencesRecord,
  patch: Partial<PtacExistingUnitsRecord>
): PtacPreferencesRecord {
  return {
    ...record,
    replacing: true,
    existing: { ...(record.existing ?? EMPTY_PTAC_EXISTING_UNITS), ...patch }
  };
}
